import { ElementRef, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Resource } from 'app/shared/acl/acl.model';
import { FeatureGroup, LatLng, LayerGroup, LeafletEvent, Map, MapOptions, TileLayer } from 'leaflet';
import 'leaflet-edgebuffer';
import { BehaviorSubject, debounceTime, distinctUntilChanged, Observable, ReplaySubject, skipWhile } from 'rxjs';
import { Basemap, DEFAULT_MAP_MAX_ZOOM, EsriTile } from '../models/basemap';
import { Bounds } from '../models/bounds';
import { CONFIG } from '../models/config';
import { Evented } from '../models/evented';
import { Position } from '../models/geometry';
import { EsriTileLayer } from '../tile.layer';


export type LeafletMapEvent = 'contextmenu' | 'click';

interface LeafletLongPressEvent extends LeafletEvent {
    touches: [
        {
            clientX: number,
            clientY: number,
        }
    ];
}


const isEsri = (tile: Basemap['tiles'][0]): tile is EsriTile => (tile as EsriTile).type === 'ESRI';


const OSM_ROAD: Basemap = {
    key: 'osm-road',
    class: 'road',
    title: 'Open Street Map',
    group: 'Map',
    tiles: [
        'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    ],
    maxNativeZoom: 19,
    attribution: '<a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
};

const MAPBOX_DARK: Basemap = {
    key: 'mapbox-dark',
    class: 'road',
    title: 'Mapbox Dark',
    group: 'Map',
    tiles: [
        `https://api.mapbox.com/styles/v1/mapbox/dark-v9/tiles/256/{z}/{x}/{y}?access_token={accessKey}`,
    ],
    attribution: '&copy; <a href="https://www.mapbox.com" target="_blank">mapbox</a>',
};

const MAPBOX_NAV_DARK: Basemap = {
    key: 'mapbox-nav-dark',
    class: 'road',
    title: 'Mapbox Navigation - Dark',
    group: 'Map',
    tiles: [
        {
            src: `https://api.mapbox.com/styles/v1/mapbox/navigation-guidance-night-v4/tiles/256/{z}/{x}/{y}?access_token={accessKey}`,
            attribution: '&copy; <a href="https://www.mapbox.com" target="_blank">mapbox</a>',
        }
    ],
};

const MAPBOX_PMBC: Basemap = {
    key: 'mapbox-pmbc',
    class: 'road',
    title: 'Mapbox PMBC',
    group: 'Map',
    tiles: [
        `https://api.mapbox.com/styles/v1/obliquity/ciuoscua100aj2iqoo5o3sjw7/tiles/256/{z}/{x}/{y}?access_token={accessKey}`,
    ],
    aclResourceName: Resource.TESTING,
    attribution: '&copy; <a href="https://www.mapbox.com" target="_blank">mapbox</a>',
};

const ESRI_ROAD: Basemap = {
    key: 'esri-road',
    class: 'road',
    title: 'ESRI Road',
    group: 'Map',
    tiles: [
        {
            src: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
            maxNativeZoom: 19,
        }
    ],
    attribution: `Esri, HERE, Garmin, USGS, Intermap, INCREMENT P, NRCAN, Esri Japan, METI,
        Esri China (Hong Kong), NOSTRA, © OpenStreetMap contributors, and the GIS User Community`,
};

const ESRI_AERIAL: Basemap = {
    key: 'esri-aerial',
    class: 'aerial',
    title: 'ESRI Aerial',
    group: 'Aerial',
    tiles: [
        {
            type: 'ESRI',
            imagerySet: 'World_Imagery',
        },
        'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}',
        'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
    ],
    attribution: `Tiles &copy; Esri &mdash; Source: Esri, i-cubed,
        USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community`,
};

const MAPBOX_AERIAL: Basemap = {
    key: 'mapbox-aerial',
    class: 'aerial',
    title: 'Mapbox Aerial',
    group: 'Aerial',
    tiles: [
        `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v10/tiles/256/{z}/{x}/{y}?access_token={accessKey}`,
    ],
    attribution: '&copy; <a href="https://www.mapbox.com" target="_blank">mapbox</a>',
    maxNativeZoom: 20, // 21 is provided as well, but same quality as 20 zoomed in
};


export const basemaps = [
    OSM_ROAD,
    MAPBOX_DARK,
    MAPBOX_NAV_DARK,
    MAPBOX_PMBC,
    ESRI_ROAD,
    ESRI_AERIAL,
    MAPBOX_AERIAL,
];


export const cmaBasemaps = [
    OSM_ROAD,
    ESRI_AERIAL,
    MAPBOX_AERIAL,
];


@Injectable()
export class LMapService extends Evented<LeafletMapEvent> {

    protected map: Map;

    protected basemap: LayerGroup;

    protected featureGroup: FeatureGroup;

    protected groups: { [name: string]: LayerGroup } = {};

    protected mapReady = false;

    protected _center = new ReplaySubject<Position>(1);

    protected _bounds = new BehaviorSubject<Bounds>({ topLeft: [0, 0], bottomRight: [0, 0], zoom: 1 });

    private timer?: number;

    private fireLongPressEvent(originalEvent: LeafletLongPressEvent, mapElement: ElementRef) {
        this.clearLongPressTimer();
        const el = originalEvent.target;
        const x = originalEvent.touches[0].clientX;
        const y = originalEvent.touches[0].clientY;

        const event = new MouseEvent('contextmenu', {
            bubbles: true,
            cancelable: true,
            clientX: x,
            clientY: y,
        });

        const suppressClickEvent = el.dispatchEvent.call(el, event);

        if (suppressClickEvent) {
            // temporarily intercept and clear the next click
            mapElement.nativeElement.addEventListener('touchend', this.clearMouseUp.bind(this, event, mapElement), true);
        }
    }

    private clearMouseUp(e: Event, mapElement: ElementRef<any>) {
        mapElement.nativeElement.removeEventListener('touchend', this.clearMouseUp, true);
        this.cancelEvent(e);
    }

    private clearLongPressTimer() {
        if (!this.timer) {
            return;
        }
        window.clearTimeout(this.timer);
        this.timer = undefined;
    }

    private cancelEvent(e: Event) {
        e.stopImmediatePropagation();
        e.preventDefault();
        e.stopPropagation();
    }

    private startLongPressTimer(e: LeafletLongPressEvent, mapElement: ElementRef<any>) {
        this.clearLongPressTimer();
        this.timer = window.setTimeout(this.fireLongPressEvent.bind(this, e, mapElement), 600);
    }

    get center$(): Observable<Position> {
        return this._center.pipe(skipWhile(() => !this.mapReady), distinctUntilChanged(), debounceTime(1000));
        // .distinctUntilChanged((prev, current) => prev[0] === current[0] && prev[1] === current[1]);
    }

    zoom = signal(1);

    maxZoom = signal(1);

    minZoom = signal(1);

    get zoom$(): Observable<number> {
        return toObservable(this.zoom);
    }

    private config = inject(CONFIG);

    getBounds(): Observable<Bounds> {
        return this._bounds.pipe(skipWhile(() => !this.mapReady));
    }

    createMap(element: ElementRef) {
        this.map = new Map(element.nativeElement, {
            zoomControl: false,
            boxZoom: false,
            worldCopyJump: true,
            attributionControl: true,
            visualClick: true,
            scrollWheelZoom: false,
            smoothWheelZoom: true,
            zoomSnap: 0,
        } as MapOptions);
        this.map.attributionControl.setPrefix('<a href="https://leaflet.com" target="_blank">leaflet</a>');

        const updateBounds = () => {
            const bounds = this.map.getBounds();
            const size = this.map.getSize();
            if (size.x === 0 || size.y === 0) {
                return;
            }
            const topLeft = bounds.getNorthWest();
            const bottomRight = bounds.getSouthEast();
            this._bounds.next({
                topLeft: [topLeft.lng, topLeft.lat],
                bottomRight: [bottomRight.lng, bottomRight.lat],
                zoom: this.map.getZoom(),
            });
        };

        const updateCenter = () => {
            if (!this.mapReady) {
                return;
            }
            const center = this.map.getCenter();
            this._center.next([center.lng, center.lat]);
        };

        const updateZoom = () => {
            if (!this.mapReady) {
                return;
            }
            this.zoom.set(this.map.getZoom());
        };

        this.mapReady = false;

        this.map.whenReady(() => {
            this.mapReady = true;
            updateBounds();
            updateCenter();
            updateZoom();
        });

        this.map.on('contextmenu', (event: any) => {
            this.fire('contextmenu', [event.latlng.lng, event.latlng.lat]);
        });

        this.map.on('click', (event: any) => {
            if (!event.latlng) {
                return;
            }
            this.fire('click', [event.latlng.lng, event.latlng.lat]);
        });

        this.map.on('moveend', updateCenter);

        this.map.on('move', updateBounds);

        this.map.on('zoomend', updateZoom);

        this.featureGroup = new FeatureGroup();
        this.map.addLayer(this.featureGroup);

        if ( / OS 13_/.test( navigator.userAgent )) {
            this.fixLongPress(element);
        }

        this.map.on('zoomlevelschange', () => {
            this.maxZoom.set(this.map.getMaxZoom());
            this.minZoom.set(this.map.getMinZoom());
        });
    }

    fixLongPress(element: ElementRef<any>) {
        // hook events that clear a pending long press event
        element.nativeElement.addEventListener('touchcancel', this.clearLongPressTimer.bind(this), true);
        element.nativeElement.addEventListener('touchend', this.clearLongPressTimer.bind(this), true);
        element.nativeElement.addEventListener('touchmove', this.clearLongPressTimer.bind(this), true);

        // hook events that can trigger a long press event
        element.nativeElement.addEventListener('touchstart', (e: LeafletLongPressEvent) => {
            this.startLongPressTimer(e, element);
        }, true);
    }

    getMinZoom() {
        return this.map.getMinZoom();
    }

    getMaxZoom() {
        return this.map.getMaxZoom();
    }

    addGroup(name: string, group: L.LayerGroup) {
        this.groups[name] = group;
        group.addTo(this.map);
    }

    getGroup(name: string) {
        return this.groups[name];
    }

    setCenter(center: Position) {
        this.map.setView(new LatLng(center[1], center[0]));
    }

    setZoom(zoom: number) {
        this.map.setZoom(zoom);
    }

    setBasemap(basemap: Basemap) {
        if (this.basemap) {
            this.basemap.remove();
        }
        this.basemap = new LayerGroup(basemap.tiles.map(tile => {
            if (typeof tile === 'string') {
                tile = {
                    src: tile,
                };
            }
            if (isEsri(tile)) {
                return new EsriTileLayer(tile.imagerySet, { maxZoom: DEFAULT_MAP_MAX_ZOOM });
            }
            const options = {
                maxZoom: DEFAULT_MAP_MAX_ZOOM,
                maxNativeZoom: tile.maxNativeZoom ?? basemap.maxNativeZoom,
                attribution: tile.attribution ?? basemap.attribution,
                edgeBufferTiles: tile.edgeBufferTiles ?? basemap.edgeBufferTiles,
                accessKey: basemap.key.startsWith('mapbox') ? this.config.mapboxApiKey : null,
                referrerPolicy: 'origin',
            } as const;
            return new TileLayer(tile.src, options);
        }));
        this.basemap.addTo(this.map);
    }

    addControl(control: any) {
        this.map.addControl(control);
    }

    getMap(): L.Map {
        return this.map;
    }

    addFeature(layer: any) {
        this.featureGroup.addLayer(layer);
    }
}
